import { useQueries } from "@tanstack/react-query";
import {
  fetchAPI,
  TASKS_SERVICE_URL,
} from "@coworker/app/src/hooks/API/tasks.service.helper";
import { Item } from "../../types/item";
import { isChineseEnvironment } from "@coworker/reusable/helpers/devices";
import { formatProductIdWithDots } from "@coworker/reusable/formatters/product.formatter";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";
import { fetchItemInfo } from "./useItemInfo";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";

const NUMBER_OF_ITEMS_PER_CALL = 40; // Selling Range (SR) has a limit of 50 items per call, but we use 40 to avoid hitting the limit.
const undefinedWord = "undefined";

export async function fetchItemsInfo(itemNos: string): Promise<Item[]> {
  if (!itemNos) {
    return [];
  }

  const isChina = isChineseEnvironment();
  const itemNoList = itemNos
    .split(",")
    .map((no) => no.trim())
    .filter((no) => no && no !== undefinedWord);

  if (itemNos.includes(undefinedWord)) {
    reportMessageToSentry(`Item numbers contain undefined`, {
      level: "info",
      extra: { itemNos, itemNoList },
    });
  }

  const chunkSize = NUMBER_OF_ITEMS_PER_CALL;
  const chunks = Array.from(
    { length: Math.ceil(itemNoList.length / chunkSize) },
    (_, index) => itemNoList.slice(index * chunkSize, (index + 1) * chunkSize)
  );

  const results: Item[] = [];

  const chunkPromises = chunks
    .filter((c) => c.length > 0)
    .map(async (chunk) => {
      const url = `${TASKS_SERVICE_URL}/items/${
        isChina ? "items-info-basic" : "items-info"
      }?itemNos=${chunk.join(",")}`;

      const { result } = await fetchAPI("GET", url);
      return (
        result?.map((item: Item) => {
          return {
            ...item,
            noFormatted: formatProductIdWithDots(item?.no),
          } as Item;
        }) ?? []
      );
    });

  const chunkResults = await Promise.all(chunkPromises);
  chunkResults.forEach((chunk) => results.push(...chunk));

  return results;
}

export const useItemsInfoQueries = (itemNos: string[]) => {
  const uniqueItemNos = [...new Set(itemNos)];
  return useQueries({
    queries:
      uniqueItemNos?.map((itemNo) => {
        return {
          queryKey: ["item", "info", itemNo],
          queryFn: () => fetchItemInfo(itemNo),
          refetchOnWindowFocus: false,
          staleTime: INTERVAL.DAY,
        };
      }) ?? [],
  });
};
